import { createContext, useContext, useSyncExternalStore } from "react";
import { scheduleOnUI } from "@-/com.npmjs.react-native-worklets";
import type { PrimaryNavigationDestination } from "@-/dev.improve.haniverse.database.schemas.actor.schema._tables.user";
import {
  cancelPrimaryNavigationCustomizationDrag,
  type PrimaryNavigationCustomizationWorklets,
} from "#primary-navigation-customization-drag";
import { playPrimaryNavigationCustomizationFeedback } from "#play-primary-navigation-customization-feedback";
import { arePrimaryNavigationStripOrdersEqual } from "#primary-navigation-strip-order";

/** An open edit: the Settings page holds one while it is focused. */
export type PrimaryNavigationCustomizationSession = {
  /** Destinations offered in this session, in canonical (palette) order. */
  available: readonly PrimaryNavigationDestination[];
  /** Persists an edited order. Called for every change, from drops and from JS edits alike. */
  onCommit: (order: readonly PrimaryNavigationDestination[]) => void;
};

export type PrimaryNavigationCustomizationSnapshot = {
  /** The open session's destinations; null while nothing is being edited. */
  available: readonly PrimaryNavigationDestination[] | null;
  /** True from pickup until the dropped icon has settled. */
  isDragging: boolean;
  /** JS mirror of the strip being edited (the UI thread's `worklets.order`). */
  order: readonly PrimaryNavigationDestination[];
};

/**
 * Coordinates editing the primary navigation strip in place. The strip (which turns into its
 * editor while a session is open), the Settings palette and the drag ghost share one controller;
 * drags run entirely on the UI thread through `worklets`, and React reads the snapshot.
 */
export type PrimaryNavigationCustomizationController = {
  /**
   * A JS edit (tap, accessibility action, Reset): shown and committed at once. Ignored while a
   * drag is in flight, so a tap can never reorder the strip underneath the finger.
   */
  edit: (order: readonly PrimaryNavigationDestination[]) => void;
  getSnapshot: () => PrimaryNavigationCustomizationSnapshot;
  /** Opens an edit session and returns the function that closes it. */
  openSession: (session: PrimaryNavigationCustomizationSession) => () => void;
  /** Shows an order without committing it: the saved order, or a revert after a failed save. */
  showOrder: (order: readonly PrimaryNavigationDestination[]) => void;
  subscribe: (listener: () => void) => () => void;
  worklets: PrimaryNavigationCustomizationWorklets;
};

export function createPrimaryNavigationCustomizationController(
  shared: Omit<
    PrimaryNavigationCustomizationWorklets,
    "onCommit" | "onDraggingChange" | "onFeedback"
  >,
): PrimaryNavigationCustomizationController {
  let snapshot: PrimaryNavigationCustomizationSnapshot = {
    available: null,
    isDragging: false,
    order: [],
  };
  let session: PrimaryNavigationCustomizationSession | null = null;
  const listeners = new Set<() => void>();
  const publish = (patch: Partial<PrimaryNavigationCustomizationSnapshot>) => {
    snapshot = { ...snapshot, ...patch };
    for (const listener of listeners) {
      listener();
    }
  };
  const setOrder = (order: readonly PrimaryNavigationDestination[]) => {
    shared.order.set(order);
    if (!arePrimaryNavigationStripOrdersEqual(snapshot.order, order)) {
      publish({ order });
    }
  };
  const worklets: PrimaryNavigationCustomizationWorklets = {
    ...shared,
    onCommit: (order) => {
      // Re-assert the dropped order: a saved order shown between the drop and this callback
      // must not replace what the user just did.
      setOrder(order);
      session?.onCommit(order);
    },
    onDraggingChange: (isDragging) => {
      if (snapshot.isDragging !== isDragging) {
        publish({ isDragging });
      }
    },
    onFeedback: playPrimaryNavigationCustomizationFeedback,
  };
  return {
    edit: (order) => {
      if (session === null || snapshot.isDragging) {
        return;
      }
      setOrder(order);
      session.onCommit(order);
    },
    getSnapshot: () => snapshot,
    openSession: (next) => {
      session = next;
      shared.available.set(next.available);
      publish({ available: next.available });
      return () => {
        if (session !== next) {
          return;
        }
        session = null;
        // The strip editor and palette unmount with the session; a ghost left mid-flight would
        // otherwise hang over the screen.
        scheduleOnUI(cancelPrimaryNavigationCustomizationDrag, worklets);
        publish({ available: null, isDragging: false });
      };
    },
    showOrder: setOrder,
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    worklets,
  };
}

/**
 * The layout's controller. Null outside the native app shell (web, isolated tests), where the
 * Settings page brings its own controller and an inline strip.
 */
export const PrimaryNavigationCustomizationContext =
  createContext<PrimaryNavigationCustomizationController | null>(null);

export function usePrimaryNavigationCustomizationController(): PrimaryNavigationCustomizationController | null {
  return useContext(PrimaryNavigationCustomizationContext);
}

function subscribeToNothing() {
  return () => {};
}
function getNoSnapshot() {
  return null;
}

export function usePrimaryNavigationCustomizationSnapshot(
  controller: PrimaryNavigationCustomizationController | null,
): PrimaryNavigationCustomizationSnapshot | null {
  return useSyncExternalStore(
    controller?.subscribe ?? subscribeToNothing,
    controller?.getSnapshot ?? getNoSnapshot,
    controller?.getSnapshot ?? getNoSnapshot,
  );
}

/** Whether the strip should render its editor instead of navigating tabs. */
export function useIsPrimaryNavigationStripEditing(): boolean {
  const snapshot = usePrimaryNavigationCustomizationSnapshot(
    usePrimaryNavigationCustomizationController(),
  );
  return snapshot?.available != null;
}
